/**
 * wallet-pass — the Wallet-pass channel's template + preview model.
 *
 * A wallet pass is not a message the customer reads once — it's a card that sits
 * on their lock screen and keeps itself current. Three shapes, one per eligible
 * message context (operator decision, 2026-07-31):
 *   · ticket      → event registration confirmed (msg_confirm_reg)
 *   · appointment → booking confirmed (msg_b_confirmed)
 *   · queue       → joined the queue (msg_q_join), the one LIVE pass — it keeps
 *                   updating position/wait after issue rather than being re-sent
 *
 * What ships here is the authoring template + a faithful on-screen preview.
 * Producing an actual installable pass needs real signing infrastructure this
 * project cannot hold (Apple requires a Pass Type ID cert + WWDR chain; Google
 * Wallet needs a signed JWT against a real issuer account) — filed as REQ-117.
 */
export type WalletPassType = "ticket" | "appointment" | "queue";

export interface WalletField { label: string; value: string }

export interface WalletTemplate {
  /** Small caps label above the primary field, e.g. "EVENT". */
  primaryLabel: string;
  /** The big central line. Tokens allowed. */
  primaryValue: string;
  /** Up to two secondary fields shown side by side under the primary line. */
  secondary: WalletField[];
  /** One small auxiliary field under the secondary row. */
  aux: WalletField;
  /** Back-of-pass detail rows. */
  back: WalletField[];
  /** Token resolving to the barcode/QR payload. Empty = no barcode. */
  barcodeMessage: string;
  /** Which pass shape this template renders as. Server-declared (seed-world rows
   *  carry it); falls back to the legacy per-id map, then a label heuristic. */
  passType?: WalletPassType;
}

const T = (label: string, value: string): WalletField => ({ label, value });

export const WALLET_DEFAULTS: Record<WalletPassType, WalletTemplate> = {
  ticket: {
    primaryLabel: "Event",
    primaryValue: "{{event_name}}",
    secondary: [T("Date", "{{event_date}}"), T("Time", "{{event_time}}")],
    aux: T("Venue", "{{venue}}"),
    back: [T("Address", "{{venue_address}}"), T("Manage ticket", "{{ticket_url}}")],
    barcodeMessage: "{{ticket_url}}",
  },
  appointment: {
    primaryLabel: "Service",
    primaryValue: "{{service}}",
    secondary: [T("Date", "{{date}}"), T("Time", "{{time}}")],
    aux: T("With", "{{staff}}"),
    back: [T("Location", "{{location_address}}"), T("Manage or reschedule", "{{manage_url}}")],
    barcodeMessage: "{{manage_url}}",
  },
  queue: {
    primaryLabel: "Queue",
    primaryValue: "{{queue_name}}",
    secondary: [T("Position", "#{{position}}"), T("Est. wait", "{{est_wait}} min")],
    aux: T("Location", "{{location}}"),
    back: [T("Joined", "{{join_time}}"), T("Leave the queue", "{{rejoin_url}}")],
    barcodeMessage: "",
  },
};

/** Which eligible message carries which pass shape (operator scope decision). */
export const WALLET_FOR_MESSAGE: Record<string, WalletPassType> = {
  msg_confirm_reg: "ticket",
  msg_b_confirmed: "appointment",
  msg_q_join: "queue",
};

export const WALLET_ELIGIBLE = new Set(Object.keys(WALLET_FOR_MESSAGE));

/** Eligibility is DATA-DRIVEN (2026-08-04): any message whose row carries a wallet
 *  template is wallet-capable — the id map above remains only for the three legacy
 *  fixture rows that predate server-declared templates. */
export const walletEligibleFor = (id: string, tpl?: WalletTemplate | null): boolean =>
  !!tpl || WALLET_ELIGIBLE.has(id);
export const walletTypeFor = (id: string, tpl?: WalletTemplate | null): WalletPassType =>
  (tpl && tpl.passType)
  || WALLET_FOR_MESSAGE[id]
  || (tpl && tpl.primaryLabel === "Queue" ? "queue" : tpl && tpl.primaryLabel === "Event" ? "ticket" : "appointment");

export const WALLET_TYPE_LABEL: Record<WalletPassType, string> = {
  ticket: "Event ticket",
  appointment: "Appointment card",
  queue: "Live queue position",
};

/** What updates an already-issued pass in place, without re-sending it (operator
 *  decision, 2026-07-31: full live updates across all three shapes). */
export const WALLET_UPDATE_TRIGGER: Record<WalletPassType, string> = {
  ticket: "Pushes instantly on a time change or cancellation — the pass on their lock screen updates itself.",
  appointment: "Pushes instantly on a reschedule or cancellation — the pass on their lock screen updates itself.",
  queue: "Updates continuously as their position and estimated wait change — no re-send, ever.",
};
